<script setup lang="ts">
import { computed } from 'vue';
import BaseIcon from '@/js/account/components/base/BaseIcon.vue';
import {
  buildPaginationItems,
  getTotalPages,
} from '@/js/account/shared/lib/pagination';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    page: number
    total: number
    perPage?: number
    disabled?: boolean
  }>(),
  {
    perPage: 6,
    disabled: false,
  },
);

const emit = defineEmits<{
  'update:page': [page: number]
}>();

const cn = classNames('pagination');

const totalPages = computed(() => getTotalPages(props.total, props.perPage));

const pageItems = computed(() =>
  buildPaginationItems(props.page, totalPages.value),
);

const isVisible = computed(() => totalPages.value > 1);

const isFirstPage = computed(() => props.page <= 1);
const isLastPage = computed(() => props.page >= totalPages.value);

const isPrevDisabled = computed(() => props.disabled || isFirstPage.value);
const isNextDisabled = computed(() => props.disabled || isLastPage.value);

function goToPage(page: number): void {
  if (
    props.disabled
    || page < 1
    || page > totalPages.value
    || page === props.page
  ) {
    return;
  }

  emit('update:page', page);
}

function goToPrevious(): void {
  goToPage(props.page - 1);
}

function goToNext(): void {
  goToPage(props.page + 1);
}
</script>

<template>
  <nav v-if="isVisible" :class="cn()" aria-label="Pagination">
    <button
      type="button"
      :class="cn('btn', { prev: true, disabled: isPrevDisabled })"
      :disabled="isPrevDisabled"
      aria-label="Previous page"
      @click="goToPrevious"
    >
      <BaseIcon
        name="left"
        :width="16"
        :height="16"
        :class="cn('icon', { prev: true })"
      />
    </button>

    <div :class="cn('numbers')">
      <template v-for="(item, index) in pageItems" :key="`${item}-${index}`">
        <span
          v-if="item === 'ellipsis'"
          :class="cn('ellipsis')"
          aria-hidden="true"
        >
          …
        </span>
        <button
          v-else
          type="button"
          :class="cn('number', { active: item === page })"
          :aria-current="item === page ? 'page' : undefined"
          :disabled="disabled || item === page"
          @click="goToPage(item)"
        >
          {{ item }}
        </button>
      </template>
    </div>

    <button
      type="button"
      :class="cn('btn', { next: true, disabled: isNextDisabled })"
      :disabled="isNextDisabled"
      aria-label="Next page"
      @click="goToNext"
    >
      <BaseIcon
        name="left"
        :width="16"
        :height="16"
        :class="cn('icon', { next: true })"
      />
    </button>
  </nav>
</template>
